import React, { useCallback, useEffect, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import { IChatNotification } from "../_stores/chat/interfaces";
import { BroadcastType } from "../_stores/broadcast/enums";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindText from "library/components/_tailwind/text";
import TailwindIcon from "library/components/_tailwind/icon";
import { CustomIconName } from "library/components/_tailwind/icon/icons/enums";
import { useIntl } from "react-intl";

const ANIMATION_DURATION_MS = 4000;
const MAX_VISIBLE = 3;

const ANIMATABLE_TYPES = new Set([
  BroadcastType.Tipped,
  BroadcastType.Gift,
  BroadcastType.Buzz,
  BroadcastType.SuperBuzz,
  BroadcastType.WheelOfFun,
]);

type AnimatedNotification = {
  notification: IChatNotification;
  key: string;
  exiting: boolean;
};

const getStyle = (type?: BroadcastType) => {
  switch (type) {
    case BroadcastType.Tipped:
      return {
        background: "linear-gradient(135deg, rgba(0,200,83,0.85) 0%, rgba(76,175,80,0.85) 100%)",
        icon: CustomIconName.token as string,
      };
    case BroadcastType.Gift:
      return {
        background: "linear-gradient(135deg, rgba(42,189,118,0.85) 0%, rgba(18,74,135,0.85) 100%)",
        icon: "card_giftcard",
      };
    case BroadcastType.Buzz:
      return {
        background: "linear-gradient(135deg, rgba(242,97,255,0.85) 0%, rgba(65,47,196,0.85) 100%)",
        icon: CustomIconName.notificationBuzz as string,
      };
    case BroadcastType.SuperBuzz:
      return {
        background: "linear-gradient(135deg, rgba(242,97,255,0.9) 0%, rgba(65,47,196,0.9) 100%)",
        icon: CustomIconName.notificationSuperBuzz as string,
      };
    case BroadcastType.WheelOfFun:
      return {
        background: "linear-gradient(135deg, rgba(207,233,255,0.85) 0%, rgba(51,73,107,0.85) 100%)",
        icon: CustomIconName.wheelOfFunIcon as string,
      };
    default:
      return {
        background: "rgba(0,0,0,0.7)",
        icon: "notifications",
      };
  }
};

type Props = {
  nodeChatStore?: NodeChatStore;
  broadcastStore?: BroadcastStore;
};

const MobileBroadcastGiftAnimation: React.ComponentType<Props> = ({
  nodeChatStore,
  broadcastStore,
}) => {
  const { notifications } = nodeChatStore!;
  const { isShowStarted } = broadcastStore!;
  const [queue, setQueue] = useState<AnimatedNotification[]>([]);
  const lastCountRef = useRef(notifications.length);
  const intl = useIntl();

  const notificationCount = notifications.length;

  useEffect(() => {
    if (!isShowStarted) return;
    const newCount = notificationCount - lastCountRef.current;
    if (newCount > 0) {
      const newNotifs = notifications.slice(0, newCount);
      const animatable = newNotifs.filter(
        n => n.broadcastType !== undefined && ANIMATABLE_TYPES.has(n.broadcastType)
      );
      if (animatable.length > 0) {
        const newItems: AnimatedNotification[] = animatable.map(n => ({
          notification: n,
          key: n.id + "-" + Date.now(),
          exiting: false,
        }));
        setQueue(prev => [...prev, ...newItems].slice(-MAX_VISIBLE));
      }
    }
    lastCountRef.current = notificationCount;
  }, [notificationCount, isShowStarted]);

  const removeItem = useCallback((key: string) => {
    setQueue(prev => prev.filter(item => item.key !== key));
  }, []);

  const startExit = useCallback((key: string) => {
    setQueue(prev =>
      prev.map(item =>
        item.key === key ? { ...item, exiting: true } : item
      )
    );
  }, []);

  if (!isShowStarted || queue.length === 0) return null;

  return (
    <TailwindFlex
      position='absolute'
      inset={["left-0"]}
      width='w-4/5'
      flexDirection='flex-col'
      gap='gap-2'
      zIndex='z-10'
      padding={["px-3"]}
      style={{
        top: "35%",
        pointerEvents: "none",
      }}>
      {queue.map(item => (
        <GiftNotificationCard
          key={item.key}
          item={item}
          intl={intl}
          onStartExit={startExit}
          onRemove={removeItem}
        />
      ))}
    </TailwindFlex>
  );
};

type CardProps = {
  item: AnimatedNotification;
  intl: any;
  onStartExit: (key: string) => void;
  onRemove: (key: string) => void;
};

const GiftNotificationCard: React.FC<CardProps> = ({
  item,
  intl,
  onStartExit,
  onRemove,
}) => {
  const { notification, key, exiting } = item;
  const style = getStyle(notification.broadcastType);

  useEffect(() => {
    const exitTimer = setTimeout(() => onStartExit(key), ANIMATION_DURATION_MS - 500);
    const removeTimer = setTimeout(() => onRemove(key), ANIMATION_DURATION_MS);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [key, onStartExit, onRemove]);

  const getText = (): string => {
    if (notification.fixedContentKey) {
      return intl.formatMessage(
        { id: notification.fixedContentKey, defaultMessage: notification.content },
        notification.fixedContentParams || {}
      );
    }
    return notification.content || "";
  };

  const text = getText().replace(/<[^>]*>/g, "");

  return (
    <TailwindFlex
      width='w-auto'
      alignItems='items-center'
      gap='gap-2.5'
      borderRadius='rounded-2xl'
      padding={["px-3", "py-2.5"]}
      style={{
        background: style.background,
        backdropFilter: "blur(8px)",
        animation: exiting
          ? "giftSlideOut 0.5s ease forwards"
          : "giftSlideIn 0.4s ease forwards",
      }}>
      {notification.image ? (
        <TailwindBox
          flex='flex-none'
          display='flex'
          alignItems='items-center'
          justifyContent='justify-center'
          borderRadius='rounded-2xl'
          style={{
            width: 56,
            height: 56,
            background: "radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.1) 70%)",
            boxShadow: "0 0 15px rgba(255,255,255,0.15)",
          }}>
          <img
            src={notification.image}
            alt=''
            style={{
              width: 44,
              height: 44,
              objectFit: "contain",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.3))",
            }}
          />
        </TailwindBox>
      ) : (
        <TailwindBox
          display='flex'
          alignItems='items-center'
          justifyContent='justify-center'
          flex='flex-none'
          borderRadius='rounded-2xl'
          style={{
            width: 56,
            height: 56,
            background: "radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.1) 70%)",
            boxShadow: "0 0 15px rgba(255,255,255,0.15)",
          }}>
          <TailwindIcon
            name={style.icon}
            fontSize='text-2xl'
            textColor='text-white'
          />
        </TailwindBox>
      )}
      <TailwindFlex
        flexDirection='flex-col'
        flex='flex-1'
        style={{ minWidth: 0 }}>
        {notification.fixedContentParams?.chatfrom && (
          <TailwindText
            fontSize='text-sm'
            fontWeight='font-bold'
            textColor='text-white'
            style={{
              textShadow: "0 1px 3px rgba(0,0,0,0.3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap" as any,
            }}>
            {notification.fixedContentParams.chatfrom}
          </TailwindText>
        )}
        <TailwindText
          fontSize='text-xs'
          textColor='text-white'
          style={{
            opacity: 0.9,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap" as any,
          }}>
          {text}
        </TailwindText>
      </TailwindFlex>
    </TailwindFlex>
  );
};

export default inject(
  "nodeChatStore",
  "broadcastStore"
)(observer(MobileBroadcastGiftAnimation));
